/**
 * Bun chart theme. Data wears the bright colors (bun-data-violet/sky/amber,
 * max 3 hues per graphic); chrome stays neutral — Instrument Sans labels in
 * stone, hairline grids, ink tooltips. Works with both chart stacks in the
 * app (chart.js via react-chartjs-2, and recharts).
 *
 * Charts paint to canvas/SVG attributes, so CSS variables must be resolved
 * to concrete values at call time. That makes these helpers theme-aware but
 * snapshot-based: call them inside the component render (recharts) or re-run
 * applyBunChartDefaults + chart.update() after setBunTheme flips the theme.
 */

/** Resolve one bun CSS variable ("--bun-stone") to its current value. */
export function bunToken(name) {
  if (typeof document === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** The data palette, in order of use. Never chrome; max 3 hues per graphic. */
export function bunChartPalette() {
  return [bunToken("--bun-data-violet"), bunToken("--bun-data-sky"), bunToken("--bun-data-amber")];
}

export const BUN_CHART_FONT = "'Instrument Sans', -apple-system, 'Segoe UI', sans-serif";

/**
 * Set chart.js global defaults to the Bun chrome. Pass the Chart class from
 * "chart.js" (the caller registers its own controllers/elements as usual).
 */
export function applyBunChartDefaults(ChartJS) {
  const d = ChartJS.defaults;
  d.font = { ...d.font, family: BUN_CHART_FONT, size: 11 };
  d.color = bunToken("--bun-stone");
  d.borderColor = bunToken("--bun-line-soft");
  d.plugins.legend.labels = {
    ...d.plugins.legend.labels,
    boxWidth: 8,
    boxHeight: 8,
    usePointStyle: true,
  };
  d.plugins.tooltip = {
    ...d.plugins.tooltip,
    backgroundColor: bunToken("--bun-ink"),
    titleColor: bunToken("--bun-on-ink"),
    bodyColor: bunToken("--bun-on-ink-muted"),
    titleFont: { family: BUN_CHART_FONT, size: 12, weight: 600 },
    bodyFont: { family: BUN_CHART_FONT, size: 12 },
    cornerRadius: 8,
    padding: 10,
    displayColors: false,
  };
}

/** Props for recharts <XAxis>/<YAxis>: stone 11px labels, no axis strokes. */
export function bunAxisProps() {
  return {
    tick: { fill: bunToken("--bun-stone"), fontSize: 11, fontFamily: BUN_CHART_FONT },
    axisLine: false,
    tickLine: false,
  };
}

/** Props for recharts <CartesianGrid>: hairline horizontal rules only. */
export function bunGridProps() {
  return { stroke: bunToken("--bun-line-soft"), strokeWidth: 1, vertical: false };
}

/** contentStyle for recharts <Tooltip>: the ink chip. */
export function bunTooltipStyle() {
  return {
    background: bunToken("--bun-ink"),
    color: bunToken("--bun-on-ink"),
    border: "none",
    borderRadius: 8,
    fontFamily: BUN_CHART_FONT,
    fontSize: 12,
    padding: "8px 10px",
  };
}
